import { z } from "zod";

// Color section enum based on the data analysis
export const ColourSectionEnum = z.enum([
  "Aquas",
  "Blues",
  "Colours",
  "Darks",
  "Green",
  "Pinks",
  "Purples",
  "Reds",
  "Yellows",
]);

// Hex color validation
const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

// Base colour schema
export const ColourSchema = z.object({
  id: z.string().uuid(),
  code: z
    .string()
    .min(1, "Colour code is required")
    .max(100, "Colour code must be less than 100 characters"),
  hex: z
    .string()
    .regex(hexColorRegex, "Invalid hex color format. Must be #RRGGBB or #RGB"),
  colour_group_id: z.string().uuid("Invalid colour group ID"),
  name: z
    .string()
    .min(1, "Colour name is required")
    .max(255, "Colour name must be less than 255 characters"),

  // CMYK values (0-100) - precise decimal values
  cmyk_c: z.number().min(0).max(100),
  cmyk_m: z.number().min(0).max(100),
  cmyk_y: z.number().min(0).max(100),
  cmyk_k: z.number().min(0).max(100),

  // RGB values (0-255)
  rgb_r: z.number().int().min(0).max(255),
  rgb_g: z.number().int().min(0).max(255),
  rgb_b: z.number().int().min(0).max(255),

  // LAB values (extended range to accommodate actual colour data)
  lab_l: z.number().min(0).max(100).optional().nullable(),
  lab_a: z.number().min(-200).max(200).optional().nullable(),
  lab_b: z.number().min(-200).max(300).optional().nullable(),

  // Timestamps
  created: z.date(),
  updated: z.date(),
});

// Schema for creating new colours (without id, created, updated)
export const createColourSchema = ColourSchema.omit({
  id: true,
  created: true,
  updated: true,
});

// Schema for updating existing colours (all fields optional except id)
export const updateColourSchema = ColourSchema.partial().required({
  id: true,
});

// Schema for colour import from JSON
export const importColourSchema = z.object({
  code: z.string().min(1),
  hex: z.string().regex(hexColorRegex),
  section: z.string().min(1),
  name: z.string().min(1),
  cmyk: z.object({
    c: z.number().min(0).max(100),
    m: z.number().min(0).max(100),
    y: z.number().min(0).max(100),
    k: z.number().min(0).max(100),
  }),
  rgb: z.object({
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
  }),
  lab: z
    .object({
      l: z.number().min(0).max(100),
      a: z.number().min(-200).max(200),
      b: z.number().min(-200).max(300),
    })
    .optional(),
});

// Schema for bulk operations
export const bulkColourOperationSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, "At least one colour ID is required"),
});

// Export types
export type Colour = z.infer<typeof ColourSchema>;
export type CreateColourInput = z.infer<typeof createColourSchema>;
export type UpdateColourInput = z.infer<typeof updateColourSchema>;
export type ImportColourInput = z.infer<typeof importColourSchema>;
export type ColourSection = z.infer<typeof ColourSectionEnum>;
